// The API client. A thin one on purpose: every rule this app appears to
// enforce is enforced by the server, and anything here is a convenience for
// the person filling the form (AD-01, spec/04 §5).

export const API_BASE =
  import.meta.env['VITE_API_BASE'] ?? 'https://api.signrs.com';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });
  if (!response.ok) {
    // The API's error envelope is a contract (spec/04 §1); a body that is not
    // one means something between here and the API answered instead.
    let code = 'UNKNOWN';
    let message = `The server returned ${response.status}.`;
    try {
      const body = (await response.json()) as { error?: { code?: string; message?: string } };
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
    } catch {
      /* keep the fallback */
    }
    throw new ApiError(response.status, code, message);
  }
  return (await response.json()) as T;
}

export interface Session {
  sessionToken: string;
  sessionId: string;
  expiresAt: string;
  canSign: boolean;
  hashPresented: string | null;
  party: { id: string; role: string; name: string | null; email: string | null; status: string };
  envelope: { id: string; title: string; status: string };
}

export interface SessionField {
  id: string;
  itemId: string;
  type: string;
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  required: boolean;
  readOnly: boolean;
  filled: boolean;
}

export interface SessionEnvelope {
  envelope: { id: string; title: string; status: string };
  party: { id: string; name: string | null; email: string | null };
  canSign: boolean;
  items: { id: string; filename: string; pageCount: number | null }[];
  fields: SessionField[];
  otherParties: { id: string; role: string; name: string | null; status: string }[];
}

export interface ViewProgress {
  item: { itemId: string; complete: boolean };
  items: { itemId: string; complete: boolean }[];
  allItemsViewed: boolean;
}

export const exchange = (token: string, accessCode?: string) =>
  request<Session>('/api/v1/sign/session', {
    method: 'POST',
    body: JSON.stringify({ token, ...(accessCode === undefined ? {} : { accessCode }) }),
  });

const authed = (sessionToken: string) => ({ authorization: `Bearer ${sessionToken}` });

export const loadEnvelope = (sessionToken: string) =>
  request<SessionEnvelope>('/api/v1/sign/session/envelope', { headers: authed(sessionToken) });

export const acknowledgeAllPages = (sessionToken: string, itemId: string) =>
  request<ViewProgress>('/api/v1/sign/session/view-progress', {
    method: 'POST',
    headers: authed(sessionToken),
    body: JSON.stringify({ itemId, acknowledgedAll: true }),
  });

export interface SignerValue {
  fieldId: string;
  value: Record<string, unknown>;
}

export const complete = (
  sessionToken: string,
  body: {
    values: SignerValue[];
    presentedHash: string;
    intentStatementVersion: string;
    intentAcknowledged: true;
  },
) =>
  request<{ envelopeStatus: string; awaiting: { id: string }[] }>(
    '/api/v1/sign/session/complete',
    { method: 'POST', headers: authed(sessionToken), body: JSON.stringify(body) },
  );

export const decline = (sessionToken: string, reason?: string) =>
  request<{ declined: true }>('/api/v1/sign/session/decline', {
    method: 'POST',
    headers: authed(sessionToken),
    body: JSON.stringify(reason === undefined ? {} : { reason }),
  });

/**
 * Fetch the document and the hash the server says it served.
 *
 * The hash comes from the response header rather than being computed here:
 * what `complete` checks is the server's own record, and a client that
 * recomputed it would be echoing back its own arithmetic instead of what it
 * was told (NN-06).
 */
export async function fetchDocument(
  sessionToken: string,
): Promise<{ blob: Blob; itemSha256: string | null }> {
  const response = await fetch(`${API_BASE}/api/v1/sign/session/document`, {
    headers: authed(sessionToken),
  });
  if (!response.ok) {
    throw new ApiError(response.status, 'DOCUMENT_UNAVAILABLE', 'The document could not be loaded.');
  }
  return {
    blob: await response.blob(),
    // This item's hash, for checking the bytes we just received. Not the
    // presented hash, and not what gets echoed at complete (spec/05 §4.4).
    itemSha256: response.headers.get('x-item-sha256'),
  };
}

// ---------------------------------------------------------------------------
// First run. spec/04 §8c.
// ---------------------------------------------------------------------------

export interface SetupStatus {
  claimed: boolean;
  requiresSetupToken: boolean;
}

export const setupStatus = () => request<SetupStatus>('/api/v1/setup/status');

export interface ClaimResult {
  token: string;
  user: { id: string; email: string; name: string | null };
  organisation: { id: string; name: string };
  role: string;
}

export const claimInstance = (body: {
  email: string;
  password: string;
  organisationName: string;
  name?: string;
  setupToken?: string;
}) =>
  request<ClaimResult>('/api/v1/setup/claim', {
    method: 'POST',
    body: JSON.stringify(body),
  });

// ---------------------------------------------------------------------------
// Signing in, and the administrative settings. spec/04 §8a, §8c.
// ---------------------------------------------------------------------------

export interface AuthSession {
  token: string;
  sessionId: string;
  expiresAt: string;
  user: { id: string; email: string; name: string | null };
  organisation: { id: string; name: string };
  role: string;
  scopes: string[];
}

export const login = (email: string, password: string) =>
  request<AuthSession>('/api/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

export const logout = (sessionToken: string) =>
  request<unknown>('/api/v1/auth/logout', {
    method: 'POST',
    headers: { authorization: `Bearer ${sessionToken}` },
  });

export type SettingKey = 'storage' | 'mail' | 'timestamping';

export interface SettingView {
  key: string;
  configured: boolean;
  /** A credential is stored. Its value is never returned, to anybody. */
  hasSecret: boolean;
  value: Record<string, unknown>;
  updatedAt: string | null;
}

export const readSetting = (sessionToken: string, key: SettingKey) =>
  request<SettingView>(`/api/v1/settings/${key}`, {
    headers: { authorization: `Bearer ${sessionToken}` },
  });

export const writeSetting = (
  sessionToken: string,
  key: SettingKey,
  body: Record<string, unknown>,
) =>
  request<SettingView>(`/api/v1/settings/${key}`, {
    method: 'PUT',
    headers: { authorization: `Bearer ${sessionToken}` },
    body: JSON.stringify(body),
  });

/**
 * Prove a configuration works before saving it.
 *
 * Takes the candidate rather than reading the stored setting, because the
 * point is to test what you are about to save — saving stays a separate act
 * (spec/04 §8c rule 3).
 */
export const testSetting = (
  sessionToken: string,
  key: SettingKey,
  body: Record<string, unknown>,
) =>
  request<{ ok: boolean; detail: string }>(`/api/v1/settings/${key}/test`, {
    method: 'POST',
    headers: { authorization: `Bearer ${sessionToken}` },
    body: JSON.stringify(body),
  });

// ---------------------------------------------------------------------------
// Envelopes. spec/04 §2, §3.
// ---------------------------------------------------------------------------

export interface EnvelopeSummary {
  id: string;
  title: string;
  status: string;
  createdAt: string;
  sentAt: string | null;
  completedAt: string | null;
}

export interface EnvelopeDetail extends EnvelopeSummary {
  items: { id: string; filename: string; position: number; pageCount: number | null }[];
  parties: {
    id: string;
    role: string;
    name: string | null;
    email: string | null;
    status: string;
    signingOrder: number;
    /** Whether the link needs a code — never the code (spec/06 §10). */
    hasAccessCode: boolean;
  }[];
  fields: { id: string; type: string; partyId: string; itemId: string; page: number }[];
}

export interface ResendResult {
  partyId: string;
  status: string;
  /** `QUEUED` for an email envelope, `ISSUED` when the sender holds custody. */
  delivery: 'QUEUED' | 'ISSUED';
  /** Present only for `ISSUED`; never stored, so a lost link is resent again. */
  url: string | null;
  expiresAt: string | null;
  previousLinkInvalidated: boolean;
}

/**
 * Issue a party a fresh signing link. spec/04 §3.
 *
 * There is no body: everything about the link is already on the envelope, and
 * a body would invite changing one of those things here.
 */
export const resendLink = (sessionToken: string, envelopeId: string, partyId: string) =>
  request<ResendResult>(`/api/v1/envelopes/${envelopeId}/parties/${partyId}/resend`, {
    method: 'POST',
    headers: authed(sessionToken),
  });

export interface Release {
  commit: string;
  builtAt: string | null;
}

/**
 * Which build the API is. spec/04 §1.
 *
 * Outside `/api/v1` and unauthenticated, like `/health`: the people who most
 * need this are the ones who cannot authenticate — a signer on a link, and
 * whoever they are on the phone to.
 */
export const readApiRelease = () => request<Release>('/version');

export interface SigningStatus {
  configured: boolean;
  reason: string | null;
}

/**
 * Whether this deployment can seal. spec/04 §2.
 *
 * Read-only and deliberately not a setting: a signing key does not belong in
 * a form. It is here so an administrator can see it is missing without
 * sending an envelope to find out.
 */
export const readSigningStatus = (sessionToken: string) =>
  request<SigningStatus>('/api/v1/settings/signing', { headers: authed(sessionToken) });

export const listEnvelopes = (sessionToken: string) =>
  request<{ data: EnvelopeSummary[] }>('/api/v1/envelopes?limit=50', {
    headers: authed(sessionToken),
  });

export const readEnvelope = (sessionToken: string, id: string) =>
  request<EnvelopeDetail>(`/api/v1/envelopes/${id}`, { headers: authed(sessionToken) });

/**
 * Step one of two. The API signs a URL; the bytes go straight to storage from
 * the browser and never through the API (spec/04 §3).
 */
export const createUpload = (
  sessionToken: string,
  body: { filename: string; mimeType: string; byteLength: number },
) =>
  request<{ uploadId: string; url: string; method: 'PUT'; headers: Record<string, string> }>(
    '/api/v1/uploads',
    { method: 'POST', headers: authed(sessionToken), body: JSON.stringify(body) },
  );

/**
 * Step two: the bytes, to the signed URL, with exactly the headers it signed.
 *
 * Not `request` — this one does not talk to our API, gets no error envelope
 * back, and must not carry our authorization header to somebody else's host.
 */
export async function putUpload(
  ticket: { url: string; headers: Record<string, string> },
  file: File,
): Promise<void> {
  const response = await fetch(ticket.url, {
    method: 'PUT',
    headers: ticket.headers,
    body: file,
  });
  if (!response.ok) {
    throw new ApiError(response.status, 'UPLOAD_FAILED', `Storage refused the file (${response.status}).`);
  }
}

export const createEnvelope = (
  sessionToken: string,
  body: {
    title: string;
    items: { filename: string; uploadId: string }[];
    parties: { role: string; name?: string; email?: string }[];
  },
) =>
  request<EnvelopeDetail>('/api/v1/envelopes', {
    method: 'POST',
    headers: authed(sessionToken),
    body: JSON.stringify(body),
  });

/** Turns an uploaded file into a revision that can be signed (spec/04 §2). */
export const prepareEnvelope = (sessionToken: string, id: string) =>
  request<unknown>(`/api/v1/envelopes/${id}/prepare`, {
    method: 'POST',
    headers: authed(sessionToken),
  });

export const addFields = (
  sessionToken: string,
  id: string,
  fields: {
    type: string;
    partyId: string;
    itemId: string;
    page: number;
    x: number;
    y: number;
    w: number;
    h: number;
  }[],
) =>
  request<unknown>(`/api/v1/envelopes/${id}/fields`, {
    method: 'POST',
    headers: authed(sessionToken),
    body: JSON.stringify({ fields }),
  });

export const distributeEnvelope = (
  sessionToken: string,
  id: string,
  body: { method: 'EMAIL' | 'NONE'; message?: string },
) =>
  request<{ method: string; links?: { partyId: string; url: string }[] }>(
    `/api/v1/envelopes/${id}/distribute`,
    { method: 'POST', headers: authed(sessionToken), body: JSON.stringify(body) },
  );

export interface AuditRecord {
  eventId: string;
  seq: number;
  type: string;
  occurredAt: string;
  payload: Record<string, unknown>;
  recordHash: string;
}

export interface AuditPage {
  data: AuditRecord[];
  nextCursor: number | null;
  /**
   * What the server checked about this window, and whether it held.
   *
   * Surfaced rather than assumed: an audit trail whose verification nobody
   * looks at is a hash chain used as decoration (spec/04 §7).
   */
  verification: {
    ok: boolean;
    checked: string[];
    scope: string;
    from: number | null;
    to: number | null;
    reason?: string;
    failedAtSeq?: number;
  };
}

export const readAudit = (sessionToken: string, id: string) =>
  request<AuditPage>(`/api/v1/envelopes/${id}/audit?limit=100`, {
    headers: authed(sessionToken),
  });

/**
 * The document itself, as bytes.
 *
 * Not `request`: the response is a PDF, not the JSON envelope, so it is
 * fetched directly and handed to the browser as a download (NN-07 — nothing
 * here may inhibit saving a copy).
 */
export async function downloadItem(
  sessionToken: string,
  envelopeId: string,
  itemId: string,
): Promise<Blob> {
  const response = await fetch(
    `${API_BASE}/api/v1/envelopes/${envelopeId}/items/${itemId}/download`,
    { headers: authed(sessionToken) },
  );
  if (!response.ok) {
    let code = 'DOWNLOAD_FAILED';
    let message = `The server returned ${response.status}.`;
    try {
      const body = (await response.json()) as { error?: { code?: string; message?: string } };
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
    } catch {
      /* keep the fallback */
    }
    throw new ApiError(response.status, code, message);
  }
  return response.blob();
}
